import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { extractRange, isToday } from '../../helpers/dateTimes';
import { isPdrOrder, isAppointmentOrder } from '../../helpers/mlkActions';
import {
  getCorrectStatus,
  getExpectedServiceDateAndTimeSlot,
} from '../../helpers/order';
import {
  Order,
  OrderState,
  BlockedReason,
  CalculatedOrderStatus,
} from '../../models';
import { useOrder } from '../useOrder';

export const useIsEditAddressEnabled = () => {
  const order = useOrder();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const isEditAddressAvailable = useMemo(() => {
    if (order && cmsConfigs) {
      const calculatedOrderStatus = getCorrectStatus(order);
      return getIsEditAddressAvailable(
        order,
        calculatedOrderStatus,
        cmsConfigs.featureFlags.isEditAddressEnabled
      );
    }
    return null;
  }, [cmsConfigs, order]);

  return isEditAddressAvailable;
};

const getIsEditAddressAvailable = (
  order: Order,
  calculatedOrderStatus: CalculatedOrderStatus | undefined,
  isServiceEnabled: boolean
) => {
  const isLastAttempt = order.blockedReasons?.includes(BlockedReason.B01);
  const isPdr = isPdrOrder(order);
  const isSuspended = calculatedOrderStatus === CalculatedOrderStatus.Suspended;
  const isAppointment = isAppointmentOrder(order); // Da togliere vedere riga 120
  const isEditAddressEnabled =
    order.shipperTerritoryConfiguration.uiParams.enableEditAddress;
  const isValidOrderState =
    order.status === OrderState.ToReschedule ||
    (order.status === OrderState.Blocked &&
      !isNil(order.blockedReasons) &&
      order.blockedReasons?.includes(BlockedReason.B03));

  const expectedDateAndTimeSlot = getExpectedServiceDateAndTimeSlot(order);
  let isSameDayPdr = false;
  if (expectedDateAndTimeSlot) {
    const today = dayjs();
    const hoursDifference = expectedDateAndTimeSlot.to
      .hour(0)
      .minute(0)
      .second(0)
      .diff(today, 'hour');
    isSameDayPdr = hoursDifference <= 0 && !!isPdr;
  }

  const isValidCalculatedStatus =
    calculatedOrderStatus !== undefined &&
    (calculatedOrderStatus === CalculatedOrderStatus.TooSoon ||
      calculatedOrderStatus === CalculatedOrderStatus.Masked ||
      calculatedOrderStatus === CalculatedOrderStatus.Missed ||
      calculatedOrderStatus === CalculatedOrderStatus.ProtectionMode ||
      calculatedOrderStatus === CalculatedOrderStatus.StandBy ||
      calculatedOrderStatus === CalculatedOrderStatus.InTransit ||
      calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.ScheduledReception);

  // Disable address IFF there is an appointment and is the delivery day
  let tooLateBecauseAppointmentBooked = false;
  if (
    isAppointment &&
    (calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.ScheduledReception) &&
    (order.expectedServiceDateAndTimeSlot || order.funnel)
  ) {
    const expectedServiceDateTimeRange = extractRange(
      order.funnel || order.expectedServiceDateAndTimeSlot
    );
    const isNotInTime = isToday(expectedServiceDateTimeRange.from);
    tooLateBecauseAppointmentBooked = isNotInTime;
  }

  return (
    !isLastAttempt &&
    !tooLateBecauseAppointmentBooked &&
    !isSameDayPdr &&
    !isSuspended &&
    isEditAddressEnabled &&
    (isValidOrderState || isValidCalculatedStatus) &&
    isServiceEnabled
  );
};
